import React, { useCallback, useMemo } from "react";
import Badge from "../../theme/untitled/Badge/Badge";
import Icon from "../../theme/untitled/icons/Icon";
import RowActionsMenu from "../../theme/untitled/RowActionsMenu/RowActionsMenu";
import OrgLogo from "./OrgLogo";
import { getOrganizationStatus } from "./organizationStatus";
import { getPlanSubscriptionName } from "../../../../helpers/subscription";
import { UserRoles } from "../../consts/crm";
import { dateTimeFormatter } from "../../helpers/admin";
import { getUploadResourceUrl } from "../../../../helpers/url";
import styles from "./OrganizationsPage.module.scss";

const formatTimestamp = (seconds) =>
  seconds ? (
    dateTimeFormatter.format(new Date(seconds * 1000))
  ) : (
    <span className={styles.dash}>&mdash;</span>
  );

/**
 * @param {boolean} disabled
 * @param {function} onAutoLogin
 * @param {function} onDelete
 * @param {function} onShowDetails
 * @param {function} onRefreshIntercomTags
 * @param {Object} organization
 * @param {string} organization.uuid
 * @param {string} organization.name
 * @param {Object=} organization.settings - settings.favicon is the uploaded logo path, if set
 * @param {string} organization.treasurer_email
 * @param {string=} organization.stripe_customer_id
 * @param {Array} organization.users
 * @param {Array} organization.organizations_plans - ordered DESC by created; [0] is the current plan
 */
const TableRow = ({
  organization,
  disabled,
  onAutoLogin,
  onShowDetails,
  onRefreshIntercomTags,
  onDelete,
}) => {
  const onUpdateOrganization = useCallback(
    (callback) => () => callback(organization),
    [organization]
  );

  const { name, treasurer_email, organizations_plans } = organization;

  const logoUrl = organization.settings?.favicon
    ? getUploadResourceUrl(organization.settings.favicon)
    : null;

  const firstPaidPlan = organizations_plans?.find((i) => i.price);
  const currentPlan =
    organizations_plans && organizations_plans.length
      ? organizations_plans[0]
      : null;

  const status = getOrganizationStatus(currentPlan);
  const planName = getPlanSubscriptionName(currentPlan);

  const ownerAccount = useMemo(() => {
    const owner = organization.users.find(
      (orgUser) => orgUser.role === UserRoles.owner
    );
    return owner || { name: "N/A", email: "N/A" };
  }, [organization]);

  const planCell = planName ? (
    <>
      {planName}
      <span className={styles.planInterval}>
        {" "}
        · {Number(currentPlan.is_annual) ? "Yearly" : "Monthly"}
      </span>
    </>
  ) : (
    <span className={styles.dash}>&mdash;</span>
  );

  const menuOptions = useMemo(
    () => [
      {
        key: "details",
        label: "Details",
        icon: <Icon name="Building07" size={20} />,
        onClick: onUpdateOrganization(onShowDetails),
      },
      {
        key: "autologin",
        label: "Auto login as owner",
        icon: <Icon name="Key01" size={20} />,
        onClick: onUpdateOrganization(onAutoLogin),
      },
      {
        key: "stripe",
        label: "View in Stripe",
        icon: <Icon name="ArrowUpRight" size={20} />,
        onClick: () =>
          window.open(
            `https://dashboard.stripe.com/customers/${organization.stripe_customer_id}`,
            "_blank"
          ),
      },
      {
        key: "refresh-tags",
        label: "Refresh Intercom tags",
        icon: <Icon name="RefreshCcw01" size={20} />,
        onClick: onUpdateOrganization(onRefreshIntercomTags),
      },
      { key: "divider", type: "divider" },
      {
        key: "delete",
        label: "Delete",
        icon: <Icon name="Trash01" size={20} />,
        destructive: true,
        onClick: onUpdateOrganization(onDelete),
      },
    ],
    [
      onUpdateOrganization,
      onShowDetails,
      onAutoLogin,
      organization.stripe_customer_id,
      onRefreshIntercomTags,
      onDelete,
    ]
  );

  return (
    <tr className={styles.row}>
      <td>
        <div className={styles.nameCell}>
          <OrgLogo name={name} iconUrl={logoUrl} />
          <span className={styles.nameText}>{name}</span>
        </div>
      </td>
      <td>
        <Badge color={status.color} size="sm" dot>
          {status.label}
        </Badge>
      </td>
      <td>{treasurer_email}</td>
      <td>{organization.users.length}</td>
      <td>
        <span className={styles.ownerName}>{ownerAccount.name}</span>
        <span className={styles.ownerEmail}>{ownerAccount.email}</span>
      </td>
      <td className={styles.dateCell}>
        {formatTimestamp(firstPaidPlan?.created)}
      </td>
      <td>{planCell}</td>
      <td className={styles.dateCell}>
        {formatTimestamp(currentPlan?.expiry)}
      </td>
      <td>
        <RowActionsMenu items={menuOptions} disabled={disabled} />
      </td>
    </tr>
  );
};

export default TableRow;
